/**
 * "+ condition" — pick a condition kind, then fill it in. Metric conditions
 * are visible but disabled in Phase 2 (the backend rejects them): the
 * workaround is a List Tag Rule whose tag is referenced here (one list selection per rule).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useRef, useState } from 'react';
import {
  Archive,
  Gauge,
  Plus,
  Sparkles,
  Tag,
  TrendingUp,
  Type,
} from 'lucide-react';

import type { StaticTag } from '@/hooks/smswiser/static-tags/useStaticTags';

import type { ListGroupCondition } from '../../list-groups.types';
import { ListsConditionEditor } from './lists';
import { listsMenuLabel } from './lists/lists-menu-label';
import { NameConditionEditor } from './NameConditionEditor';
import { TagConditionEditor } from './TagConditionEditor';

type Step = 'closed' | 'menu' | 'static' | 'perf' | 'name' | 'lists';

interface Props {
  staticTags: StaticTag[];
  countryKey?: string;
  selectedLists?: { listIds: number[] }; // the rule's one list selection — the menu edits it
  onAdd: (condition: ListGroupCondition) => void;
}

const MENU: Array<{
  step: Step;
  label: string;
  desc: string;
  Icon: typeof Tag;
}> = [
  { step: 'static', label: 'Has static tag', desc: 'a tag you assigned by hand', Icon: Tag },
  { step: 'perf', label: 'Has performance tag', desc: 'a tag earned from its stats', Icon: TrendingUp },
  { step: 'lists', label: 'Select lists…', desc: 'search and choose specific lists', Icon: Archive },
  { step: 'name', label: 'Name contains…', desc: 'match words in the list name', Icon: Type },
];

const ITEM_CLASS =
  'flex w-full items-start gap-2.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted';
const ICON_CLASS = 'mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground';
const DESC_CLASS = 'block text-[11px] font-normal text-muted-foreground';

export const AddConditionMenu: FC<Props> = ({ staticTags, onAdd, countryKey, selectedLists }) => {
  const [step, setStep] = useState<Step>('closed');
  const [opensUp, setOpensUp] = useState(false);
  const anchorRef = useRef<HTMLSpanElement | null>(null);

  const add = (condition: ListGroupCondition) => {
    onAdd(condition);
    setStep('closed');
  };

  const toggle = () => {
    if (step !== 'closed') return setStep('closed');
    // Flip upward when the viewport has more room above than below the button,
    // so the menu never runs off the bottom of the screen.
    const rect = anchorRef.current?.getBoundingClientRect();
    const below = rect ? window.innerHeight - rect.bottom : Infinity;
    setOpensUp(Boolean(rect) && below < 380 && rect!.top > below);
    setStep('menu');
  };

  return (
    <span ref={anchorRef} className="relative inline-flex">
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1 rounded-lg border border-dashed border-input px-2.5 py-1 text-xs font-medium text-muted-foreground hover:border-primary/50 hover:text-foreground"
      >
        <Plus className="h-3 w-3" /> condition
      </button>
      {step !== 'closed' ? (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setStep('closed')}
          />
          <div
            className={`absolute left-0 z-50 max-h-[70vh] overflow-y-auto rounded-lg border border-border bg-popover shadow-lg ${
              opensUp ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
          >
            {step === 'menu' ? (
              <div className="flex w-64 flex-col p-1">
                {MENU.map(({ step: itemStep, label, desc, Icon }) => (
                  <button key={itemStep} type="button" onClick={() => setStep(itemStep)} className={ITEM_CLASS}>
                    <Icon className={ICON_CLASS} />
                    <span className="font-medium">
                      {itemStep === 'lists' ? listsMenuLabel(selectedLists).label : label}
                      <span className={DESC_CLASS}>{itemStep === 'lists' ? listsMenuLabel(selectedLists).desc : desc}</span>
                    </span>
                  </button>
                ))}
                <button type="button" onClick={() => add({ type: 'tag', tag: 'New' })} className={ITEM_CLASS}>
                  <Sparkles className={ICON_CLASS} />
                  <span className="font-medium">
                    Is New
                    <span className={DESC_CLASS}>first seen in the last days</span>
                  </span>
                </button>
                <button type="button" onClick={() => add({ type: 'tag', tag: 'Unused' })} className={ITEM_CLASS}>
                  <Archive className={ICON_CLASS} />
                  <span className="font-medium">
                    Is Unused
                    <span className={DESC_CLASS}>never been sent to</span>
                  </span>
                </button>
                <button
                  type="button"
                  disabled
                  title="Coming with the planner — use a List Tag Rule's tag instead"
                  className="flex w-full cursor-not-allowed items-start gap-2.5 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground/50"
                >
                  <Gauge className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span className="font-medium">
                    Metric threshold…
                    <span className="block text-[11px] font-normal">coming with the planner</span>
                  </span>
                </button>
              </div>
            ) : step === 'lists' ? (
              <ListsConditionEditor countryKey={countryKey ?? ''} initial={selectedLists} onApply={add} />
            ) : step === 'name' ? (
              <NameConditionEditor onApply={add} />
            ) : (
              <TagConditionEditor
                mode={step}
                staticTags={staticTags}
                onApply={(tag) => add({ type: 'tag', tag })}
              />
            )}
          </div>
        </>
      ) : null}
    </span>
  );
};
